import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
} from "@heroui/react";
import { useEffect, useState } from "react";

import type { ProjectItemInput } from "./api";

/**
 * 案件明細の追加ダイアログ。
 *
 * 見積明細のダイアログと同じ考え方。案件明細は**必ず図面行**なので図番が要る。
 * 図番を入れると**その都度新しい図面を 1 枚作る**（登録済みの図面から選ぶ形は
 * 採っていない。同じ図番の行が何行あってもよい）。図面を明細ごとに持たせて
 * いるため、図番・品名は追加後に一覧のセルから直せる。
 *
 * 数量・納期・出荷予定日・摘要も一覧のセルで直接編集できる。ここに置くのは
 * 「行を起こす時点で分かっているもの」だけ（発送日は実績なので置かない）。
 */
export default function ProjectItemDialog({
  isOpen,
  defaultDeadline,
  defaultShippingPlannedDate,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  /** 追加時の納期の初期値（案件の納期）。 */
  defaultDeadline?: string | null;
  /**
   * 追加時の出荷予定日の初期値（案件の出荷予定日）。
   *
   * 受注で起こした行には案件の値が写っているので、後から足した行だけ空になると
   * 同じ案件の中で揃わない。
   */
  defaultShippingPlannedDate?: string | null;
  onClose: () => void;
  /** 保存する。エラー文言を返すと閉じずに表示する。 */
  onSubmit: (input: ProjectItemInput) => Promise<string | null>;
}) {
  const [drawingNumber, setDrawingNumber] = useState("");
  const [itemName, setItemName] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [deadline, setDeadline] = useState("");
  const [machiningDeadline, setMachiningDeadline] = useState("");
  const [shippingPlannedDate, setShippingPlannedDate] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // 開くたびに空に戻す（前回の入力を持ち越さない）。
  useEffect(() => {
    if (!isOpen) return;
    setDrawingNumber("");
    setItemName("");
    setQuantity("1");
    setDeadline(defaultDeadline ?? "");
    // 加工納期は案件から写せない（社内で決める期限なので、既定を置くと
    // 「決めていない」行が納期と同じ日で埋まってしまう）。
    setMachiningDeadline("");
    setShippingPlannedDate(defaultShippingPlannedDate ?? "");
    setNote("");
    setError(null);
    setSaving(false);
  }, [isOpen, defaultDeadline, defaultShippingPlannedDate]);

  const submit = async () => {
    const typed = drawingNumber.trim();
    if (!typed) {
      setError("図番を入力してください。");
      return;
    }
    const parsedQuantity = Number(quantity);
    if (!Number.isInteger(parsedQuantity) || parsedQuantity < 1) {
      setError("数量は 1 以上の整数で入力してください。");
      return;
    }

    setSaving(true);
    const message = await onSubmit({
      drawing_number: typed,
      item_name: itemName.trim() || null,
      quantity: parsedQuantity,
      deadline: deadline || null,
      machining_deadline: machiningDeadline || null,
      shipping_planned_date: shippingPlannedDate || null,
      note: note.trim() || null,
    });
    setSaving(false);
    if (message) {
      setError(message);
      return;
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>明細の追加</span>
          <span className="text-small font-normal text-default-500">
            案件明細は必ず図面行です。図面を 1
            枚新しく登録します（同じ図番の行が何行あってもかまいません）。
          </span>
        </ModalHeader>

        <ModalBody>
          <Input
            label="図番"
            labelPlacement="outside"
            placeholder=" "
            aria-label="図番"
            variant="bordered"
            radius="sm"
            value={drawingNumber}
            description="追加したあとに一覧のセルから直せます。"
            onValueChange={(v) => {
              setDrawingNumber(v);
              setError(null);
            }}
          />

          <Input
            label="品名"
            labelPlacement="outside"
            placeholder=" "
            aria-label="品名"
            variant="bordered"
            radius="sm"
            value={itemName}
            description="省くと図番を品名として入れておきます。"
            onValueChange={(v) => {
              setItemName(v);
              setError(null);
            }}
          />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="数量"
              labelPlacement="outside"
              placeholder=" "
              aria-label="数量"
              type="number"
              min={1}
              step={1}
              variant="bordered"
              radius="sm"
              value={quantity}
              onValueChange={(v) => {
                setQuantity(v);
                setError(null);
              }}
            />
            <Input
              label="納期"
              labelPlacement="outside"
              placeholder=" "
              aria-label="納期"
              type="date"
              variant="bordered"
              radius="sm"
              value={deadline}
              description="未指定なら案件の納期に従います。"
              onValueChange={setDeadline}
            />
            <Input
              label="加工納期"
              labelPlacement="outside"
              placeholder=" "
              aria-label="加工納期"
              type="date"
              variant="bordered"
              radius="sm"
              value={machiningDeadline}
              description="社内で加工を上げる期限。現場へ出す図面にはこちらを刷ります。"
              onValueChange={setMachiningDeadline}
            />
            {/* 発送日はここに置かない（実績なので、出したあとに一覧のセルで入れる）。 */}
            <Input
              label="出荷予定日"
              labelPlacement="outside"
              placeholder=" "
              aria-label="出荷予定日"
              type="date"
              variant="bordered"
              radius="sm"
              value={shippingPlannedDate}
              description="未指定なら案件の出荷予定日に従います。"
              onValueChange={setShippingPlannedDate}
            />
          </div>

          <Textarea
            label="摘要"
            labelPlacement="outside"
            placeholder=" "
            aria-label="摘要"
            variant="bordered"
            radius="sm"
            minRows={2}
            value={note}
            onValueChange={setNote}
          />

          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="item-submit"
            isLoading={saving}
            onPress={() => void submit()}
          >
            追加
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
